import dayjs, { Dayjs } from 'dayjs';
import {
  PROMOTION_SCHEDULE_STATUS,
  PromotionSchedule,
  PromotionScheduleCoupon,
  PromotionScheduleTrack,
  PromotionScheduleWeekMeta,
} from '../../../../lib/api/promotionSchedule';
import { COUPON_DISCOUNT_TYPE, PERIOD_UNIT, PeriodUnit, TRACK_META } from '../constants';
import { formatKst, getTodayKst, toKstDate } from './kst';

export const DATE_FORMAT = 'YYYY-MM-DD';
export const TIME_FORMAT = 'HH:mm';

/**
 * 기간은 **inclusive datetime** 이다 — 종일 항목은 `00:00:00` ~ `23:59:59` (백엔드 관례와 동일).
 * 별도 플래그 없이 이 경계인지로 "종일 여부"를 판정한다 (파생 가능한 값이라 저장하지 않는다).
 *
 * ⚠️ **초까지 본다.** 시·분만 비교하면 `00:00:00~23:59:00` 으로 저장된 시간 지정 항목이 종일로
 * 판정돼 시각 배지가 사라지고, 그 상태에서 다른 필드를 저장하면 `buildPeriod` 가 종료를
 * `23:59:59` 로 바꿔 **사용자가 지정한 시각이 조용히 변한다.**
 *
 * 밀리초는 보지 않는다 — MySQL `datetime` 이 소수초를 갖지 않아 응답은 항상 `.000` 이고,
 * 조건을 걸면 다른 경로로 `.999` 가 들어올 때 종일 항목이 시간 지정으로 오판된다.
 */
export const isAllDayPeriod = (startDate: string, endDate: string): boolean => {
  const start = toKstDate(startDate);
  const end = toKstDate(endDate);

  return (
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    start.getSeconds() === 0 &&
    end.getHours() === 23 &&
    end.getMinutes() === 59 &&
    end.getSeconds() === 59
  );
};

/**
 * ISO 문자열 → KST 날짜 변환 결과 캐시.
 *
 * 배치·겹침·일수 계산이 같은 ISO 문자열을 수십~수백 번 다시 변환한다. KST 변환은
 * `new Date` → `utcToZonedTime` → `format` 3단계라 1회당 수 μs 이고, 연 단위 조회(53주 × 수백 건)에서는
 * 렌더당 수만 회가 되어 체감 지연을 만든다. 같은 입력이면 항상 같은 출력이라 캐시가 안전하다.
 *
 * 상한을 두지 않는 이유 — admin 은 SPA 라 모듈 스코프 Map 은 **라우트를 옮겨도 살아 있고**
 * 새로고침해야 비워진다. 대신 자라는 폭이 조회한 항목의 날짜 수(연 단위여도 수천 개 문자열)라
 * 세션 내내 쌓여도 문제가 되는 규모가 아니다.
 */
const datePartCache = new Map<string, string>();

/**
 * 날짜 + 시각 표기 (`yyyy-MM-dd HH:mm:ss`).
 *
 * 등록·수정 일시는 **초까지 봐야 하는 값**이다 — 같은 날 여러 번 고치는 운영이라
 * 날짜만 보여주면 "누가 먼저 고쳤나" 를 알 수 없고, 리스트 정렬과도 눈으로 대조가 안 된다.
 * (기간은 날짜가 정본이라 `toDatePart` 를 그대로 쓴다.)
 */
export const toDateTimePart = (value: string): string => formatKst(value, 'yyyy-MM-dd HH:mm:ss');

/**
 * 간트·캘린더의 열 배치는 시각을 무시하고 **날짜 부분**으로만 한다.
 * 날짜는 KST 기준이다 — 브라우저 시간대를 쓰면 해외 접속 시 하루가 밀린다 (`./kst` 참조).
 */
export const toDatePart = (value: string): string => {
  const cached = datePartCache.get(value);

  if (cached !== undefined) {
    return cached;
  }

  const datePart = formatKst(value, 'yyyy-MM-dd');
  datePartCache.set(value, datePart);

  return datePart;
};

/** 시간 지정 항목의 막대·툴팁에 붙일 시각 라벨 (종일이면 null). */
export const getTimeRangeLabel = (startDate: string, endDate: string): string | null => {
  if (isAllDayPeriod(startDate, endDate)) {
    return null;
  }

  return `${formatKst(startDate, 'HH:mm')}~${formatKst(endDate, 'HH:mm')}`;
};

/** 두 구간이 겹치는지. 양쪽 모두 inclusive. */
export const isOverlapped = (aStart: string, aEnd: string, bStart: string, bEnd: string): boolean =>
  // datetime 이 섞여도 배치는 날짜 기준이라 날짜 부분으로 비교한다
  toDatePart(aStart) <= toDatePart(bEnd) && toDatePart(aEnd) >= toDatePart(bStart);

/** 주당 최소 개수 미달 — 트랙 밴드의 해당 주 구간에 표시한다. */
export type WeeklyShortage = {
  /** 주 시작일 (yyyy-MM-dd) — weeks[] 의 키와 같다 */
  weekStartDate: string;
  /** 주 종료일 (yyyy-MM-dd) — 화면에 "며칠부터 며칠까지"를 보여주려면 필요하다 */
  weekEndDate: string;
  /** 주차 라벨 (예: "8월 2주차") */
  weekLabel: string;
  /** 그 주에 진행 중인 확정 항목 수 */
  count: number;
  /** 규칙상 필요한 최소 개수 */
  required: number;
};

/**
 * 트랙별 주당 최소 개수 미달 주를 찾는다.
 *
 * 규칙(`TRACK_META.minPerWeek`)이 있는 트랙만 계산한다 — 없는 트랙은 빈 배열.
 *
 * 세는 방식은 두 가지 결정을 따른다:
 * - **겹치는 주마다 1개** — 시트가 주 단위 열이라 병합 셀이 걸친 주마다 존재했던 감각과 같다.
 *   "그 주에 진행 중인 기획전 수"를 본다 (그 주에 새로 오픈한 수가 아니다).
 * - **확정만** — "실제로 나갈 건이 모자라는가"를 보는 관점이라 미확정·취소는 세지 않는다.
 *   미확정만 쌓여 있는 주는 경고가 떠야 한다.
 *
 * ⚠️ 조회 구간의 첫/마지막 주는 구간 밖 날짜를 포함한다(서버가 겹치는 주를 모두 내려주므로).
 *   그 주의 카운트에는 화면 밖에서 시작·종료하는 항목이 섞일 수 있는데, 이는 의도한 동작이다 —
 *   운영이 보는 것은 "그 주의 실제 진행 수"이지 "화면에 보이는 막대 수"가 아니다.
 */
export const findWeeklyShortages = (
  track: PromotionScheduleTrack,
  schedules: PromotionSchedule[],
  weeks: PromotionScheduleWeekMeta[],
): WeeklyShortage[] => {
  const required = TRACK_META[track]?.minPerWeek;

  if (!required) {
    return [];
  }

  const confirmed = schedules.filter(
    (schedule) => schedule.track === track && schedule.status === PROMOTION_SCHEDULE_STATUS.CONFIRMED,
  );

  return weeks.reduce<WeeklyShortage[]>((shortages, week) => {
    const count = confirmed.filter((schedule) =>
      isOverlapped(schedule.startDate, schedule.endDate, week.startDate, week.endDate),
    ).length;

    if (count < required) {
      shortages.push({
        weekStartDate: week.startDate,
        weekEndDate: week.endDate,
        weekLabel: week.label,
        count,
        required,
      });
    }

    return shortages;
  }, []);
};
/** startDate ~ endDate 의 일수 (inclusive). 하루짜리는 1. */
export const getDayCount = (startDate: string, endDate: string): number =>
  dayjs(toDatePart(endDate)).diff(dayjs(toDatePart(startDate)), 'day') + 1;

/**
 * 타임라인이 그릴 전체 일자 범위.
 *
 * 요청한 from~to 가 아니라 **주차 메타의 첫 주 시작일 ~ 마지막 주 종료일** 을 쓴다.
 * 서버가 구간과 겹치는 주를 모두 내려주므로 주 경계가 잘리지 않는다.
 */
export const getTimelineRange = (
  weeks: PromotionScheduleWeekMeta[],
): { start: string; end: string; totalDays: number } | null => {
  if (weeks.length === 0) {
    return null;
  }

  const start = weeks[0].startDate;
  const end = weeks[weeks.length - 1].endDate;

  return { start, end, totalDays: getDayCount(start, end) };
};

/** 타임라인 시작일 기준 0-based 열 인덱스. */
export const getDayIndex = (rangeStart: string, date: string): number =>
  dayjs(toDatePart(date)).diff(dayjs(toDatePart(rangeStart)), 'day');

/**
 * 겹치는 항목이 같은 행에 오지 않도록 행을 분배한다 (greedy interval partitioning).
 *
 * 구글시트에서는 트랙마다 고정된 슬롯 행 수가 있었고(프로모션 7행) 그게 동시 진행 개수의
 * 상한이었다. 여기서는 필요한 만큼 행이 늘어나므로 그 제약이 사라진다.
 *
 * @param schedules 같은 트랙의 항목들
 * @returns 행별 항목 배열. 각 행 안에서는 기간이 겹치지 않는다.
 */
export const assignRows = (schedules: PromotionSchedule[]): PromotionSchedule[][] => {
  const sorted = [...schedules].sort((a, b) => {
    if (a.startDate !== b.startDate) {
      return a.startDate < b.startDate ? -1 : 1;
    }
    return a.id - b.id;
  });

  const rows: PromotionSchedule[][] = [];

  sorted.forEach((schedule) => {
    /**
     * 이미 놓인 마지막 항목이 이 항목보다 **하루 이상 먼저** 끝나는 행을 찾는다.
     *
     * 비교를 날짜 부분으로 하는 이유 — 간트는 열이 하루 단위라 같은 날에 시각만 다른
     * 두 항목(10~12시 / 14~16시)을 한 행에 두면 같은 칸에서 겹쳐 보인다. 행을 나눠야 읽힌다.
     */
    const availableRow = rows.find((row) => {
      const last = row[row.length - 1];
      return toDatePart(last.endDate) < toDatePart(schedule.startDate);
    });

    if (availableRow) {
      availableRow.push(schedule);
      return;
    }

    rows.push([schedule]);
  });

  return rows;
};

/** 트랙별로 그룹핑. 항목이 없는 트랙도 빈 배열로 남긴다(밴드는 항상 보여야 함). */
export const groupByTrack = (
  schedules: PromotionSchedule[],
  trackOrder: PromotionScheduleTrack[],
): { track: PromotionScheduleTrack; rows: PromotionSchedule[][] }[] =>
  trackOrder.map((track) => ({
    track,
    rows: assignRows(schedules.filter((schedule) => schedule.track === track)),
  }));

/**
 * 캘린더 뷰용 — 한 주 행 안에서 항목이 차지하는 구간.
 *
 * 한 항목이 여러 주에 걸치면 주마다 잘려 여러 세그먼트가 된다.
 * `isClippedStart`/`isClippedEnd` 로 "여기서 시작/끝난 게 아니라 이어진다"를 표시해
 * 월 경계에서도 연속임이 드러나게 한다.
 */
export type WeekSegment = {
  schedule: PromotionSchedule;
  /** 주 안에서의 시작 열 (0=월요일) */
  startColumn: number;
  /** 차지하는 일수 */
  span: number;
  isClippedStart: boolean;
  isClippedEnd: boolean;
};

/**
 * 트랙별로 행을 묶어 배치한다 — **캘린더 전용**.
 *
 * 간트는 트랙마다 밴드가 나뉘어 있어 순서가 문제되지 않는다. 캘린더는 한 주에 모든 트랙이
 * 섞여 쌓이는데, 겹침만 피해 배치하면 프로모션·브랜드·기획전이 뒤엉켜 **어느 트랙인지
 * 색으로밖에 알 수 없다.** 트랙 안에서만 겹침을 피하면 같은 트랙이 세로로 모여 덩어리로 읽힌다.
 *
 * 행 수가 조금 늘 수 있지만(트랙이 달라도 기간이 안 겹치면 한 행에 들어가던 것이 나뉜다),
 * 구분이 안 되는 쪽이 더 큰 손해다.
 */
export const assignRowsByTrack = (
  schedules: PromotionSchedule[],
  trackOrder: PromotionScheduleTrack[],
): PromotionSchedule[][] =>
  trackOrder.flatMap((track) => {
    const ofTrack = schedules.filter((schedule) => schedule.track === track);

    return ofTrack.length ? assignRows(ofTrack) : [];
  });

export const getWeekSegments = (
  week: PromotionScheduleWeekMeta,
  schedules: PromotionSchedule[],
  trackOrder?: PromotionScheduleTrack[],
): WeekSegment[][] => {
  const inWeek = schedules.filter((schedule) =>
    isOverlapped(schedule.startDate, schedule.endDate, week.startDate, week.endDate),
  );

  // 주 안에서도 겹침이 있으면 행을 나눠야 한다. 트랙 순서를 주면 트랙별로 묶는다
  const rows = trackOrder ? assignRowsByTrack(inWeek, trackOrder) : assignRows(inWeek);

  return rows.map((row) =>
    row.map((schedule) => {
      /**
       * ⚠️ 항목 기간은 **UTC ISO 문자열**, 주차 메타는 **로컬 날짜 문자열**이다.
       * 그대로 문자열 비교하면 KST 자정 항목(`…T15:00:00.000Z`)이 엉뚱하게 판정되므로
       * 반드시 날짜 부분으로 맞춘 뒤 비교한다.
       */
      const scheduleStart = toDatePart(schedule.startDate);
      const scheduleEnd = toDatePart(schedule.endDate);
      const isClippedStart = scheduleStart < week.startDate;
      const isClippedEnd = scheduleEnd > week.endDate;

      const segmentStart = isClippedStart ? week.startDate : scheduleStart;
      const segmentEnd = isClippedEnd ? week.endDate : scheduleEnd;

      return {
        schedule,
        startColumn: getDayIndex(week.startDate, segmentStart),
        span: getDayCount(segmentStart, segmentEnd),
        isClippedStart,
        isClippedEnd,
      };
    }),
  );
};

/**
 * 캘린더 뷰의 월 단위 묶음.
 * 주의 소속 월은 서버가 준 `week.month`(폴라리스 규약 = 목요일이 속한 달)를 그대로 쓴다 —
 * 프론트에서 다시 판단하면 규약이 어긋난다.
 */
export const groupWeeksByMonth = (
  weeks: PromotionScheduleWeekMeta[],
): { year: number; month: number; weeks: PromotionScheduleWeekMeta[] }[] => {
  const groups: { year: number; month: number; weeks: PromotionScheduleWeekMeta[] }[] = [];

  weeks.forEach((week) => {
    const last = groups[groups.length - 1];

    if (last && last.year === week.year && last.month === week.month) {
      last.weeks.push(week);
      return;
    }

    groups.push({ year: week.year, month: week.month, weeks: [week] });
  });

  return groups;
};

/** 주 시작일(월요일) 기준 7일의 날짜 문자열. */
export const getWeekDates = (weekStartDate: string): string[] =>
  Array.from({ length: 7 }, (_, index) => dayjs(weekStartDate).add(index, 'day').format(DATE_FORMAT));

/**
 * 단위별 기본 조회 구간. 계획은 앞을 보는 화면이라 미래를 넉넉히 잡는다.
 * - 일: 이번 달 1일 ~ 다음 달 말일
 * - 월: 이번 달 ~ 다음 달
 * - 연: 올해 ~ 올해
 */
export const getDefaultRange = (periodUnit: PeriodUnit): [Dayjs, Dayjs] => {
  // "오늘"의 기준은 KST 다 — 해외 접속 시 기본 구간이 하루 어긋나지 않게 한다
  const today = dayjs(getTodayKst());

  if (periodUnit === PERIOD_UNIT.YEAR) {
    return [today.startOf('year'), today.endOf('year')];
  }
  return [today.startOf('month'), today.add(1, 'month').endOf('month')];
};

/**
 * 선택한 범위를 실제 조회 구간으로 변환한다.
 *
 * antd RangePicker 는 `picker="month"` 일 때 그 달의 1일을, `picker="year"` 일 때 그 해 1월 1일을
 * 값으로 주기 때문에 **끝 경계를 직접 채워야 한다.** 안 채우면 "6월~7월" 선택이 6/1~7/1 로 조회된다.
 */
export const toQueryRange = (range: [Dayjs, Dayjs], periodUnit: PeriodUnit): [Dayjs, Dayjs] => {
  const [start, end] = range;

  if (periodUnit === PERIOD_UNIT.MONTH) {
    return [start.startOf('month'), end.endOf('month')];
  }
  if (periodUnit === PERIOD_UNIT.YEAR) {
    return [start.startOf('year'), end.endOf('year')];
  }
  return [start, end];
};

/**
 * 막대에 함께 노출할 부가 정보.
 *
 * 구글시트는 한 셀에 `[MD취합/15%쿠폰]` + `담당자 제목` 을 2줄로 담아 정보량이 많았다.
 * 백필 시 대괄호 블록을 memo 로 옮겼으므로, 표시할 때 다시 뽑아 배지로 보여준다.
 *
 * ⚠️ 스키마를 늘리지 않는 **표시 전용** 방식이다 — 쿠폰율로 필터·집계까지 하려면
 * 전용 컬럼이 필요하다 (memo 는 자유 텍스트라 신규 입력이 이 형식을 따를 보장이 없다).
 */
export type ScheduleMeta = {
  /** 예: "15%" */
  couponLabel: string | null;
  /** 예: ["MD취합", "역제안"] */
  sourceLabels: string[];
};

const SOURCE_KEYWORDS = ['사전기획안', 'MD취합', '역제안', '스테디', '루키'];

/**
 * 할인 표기 — 뒤따르는 `쿠폰` 까지 함께 먹는다.
 * 백필 형식 `[MD취합/15%쿠폰]` 에서 `15%쿠폰` 이 통째로 소비돼야 잔여가 비고, 그래야 잘라낼 수 있다.
 */
const DISCOUNT_TOKEN = /(\d+(?:\.\d+)?)\s*%\s*(?:쿠폰)?/g;

/** 쿠폰 한 건의 혜택 표기 — `15%` / `5,000원` */
export const formatCouponBenefit = (coupon: PromotionScheduleCoupon): string =>
  coupon.discountType === COUPON_DISCOUNT_TYPE.RATE
    ? `${coupon.discountAmount}%`
    : `${coupon.discountAmount.toLocaleString()}원`;

/**
 * 연결된 쿠폰을 **혜택 큰 순**으로 정렬한다.
 *
 * ⚠️ 서버는 `couponId` 오름차순으로 준다. 그대로 쓰면 **가장 먼저 만든 쿠폰**이 대표가 되는데,
 * 등급 쿠폰(8주년 gold 1%/2%/4%/8%)에서는 그게 **가장 작은 혜택**이다 — 기획의 얼굴인 8% 가 가려진다.
 *
 * 율과 금액은 **섞어서 크기를 비교할 수 없다**(15% 와 5,000원 중 뭐가 큰지는 주문 금액에 달렸다).
 * 그래서 종류별로 묶고, 율을 앞에 둔다 — 등급 쿠폰이 대부분 율이라 그쪽이 기획의 뼈대다.
 */
export const sortCouponsByBenefit = (coupons: PromotionScheduleCoupon[]): PromotionScheduleCoupon[] =>
  [...coupons].sort((a, b) => {
    const isRateA = a.discountType === COUPON_DISCOUNT_TYPE.RATE;
    const isRateB = b.discountType === COUPON_DISCOUNT_TYPE.RATE;

    if (isRateA !== isRateB) {
      return isRateA ? -1 : 1;
    }

    // 같은 종류끼리는 큰 혜택이 앞. 동률이면 id 순으로 안정 정렬한다
    return b.discountAmount - a.discountAmount || a.id - b.id;
  });

/**
 * 쿠폰 배지 문구 — **연결된 쿠폰 우선, 없으면 memo 파싱 fallback**.
 *
 * 연결이 있으면 실제 쿠폰이 정본이다. 연결 전(계획 단계)에는 시트에서 옮겨온
 * `[MD취합/15%쿠폰]` 표기가 memo 에 남아 있으므로 그것을 보여준다 —
 * 스케줄은 연간 계획이라 작성 시점에 쿠폰이 아직 없다.
 *
 * 여러 개면 `15% 외 2` 처럼 줄인다. 막대 배지는 폭이 좁아 전부 나열할 수 없다.
 */
export const getCouponLabel = (schedule: Pick<PromotionSchedule, 'coupons' | 'memo'>): string | null => {
  const [first, ...rest] = schedule.coupons ?? [];

  if (first) {
    return rest.length ? `${formatCouponBenefit(first)} 외 ${rest.length}` : formatCouponBenefit(first);
  }

  return extractScheduleMeta(schedule.memo).couponLabel;
};

/**
 * memo 에서 **배지로 이미 보여준 메타 블록을 뺀 나머지**.
 *
 * 백필이 시트의 `[MD취합/15%쿠폰]` 을 memo 맨 앞에 넣었고 화면은 그 블록을 파싱해 배지로 그린다.
 * 원문을 그대로 또 보여주면 같은 문구가 두 번 나온다 — memo 가 블록 하나뿐이면 통째로 중복이다.
 *
 * ⚠️ **"뭔가 뽑혔으면 자른다" 로는 안 된다.** 뽑기(`extractScheduleMeta`)는 블록 안 전체를 훑고
 * 출처 키워드도 부분일치라, 자유 서술이 걸려든다 — `[참여 브랜드 상품 25% 쿠폰]` 은 `25%` 때문에,
 * `[스테디셀러 기획전]` 은 `스테디` 때문에 통째로 사라진다. memo 는 자유 텍스트라 형식 보장이 없다.
 *
 * 그래서 **배지로 보여준 토큰과 구분자를 빼고도 말이 남는지**로 판정한다. 남으면 사람이 쓴
 * 문장이니 원문을 그대로 두고, 남지 않으면 배지와 완전히 겹치므로 잘라낸다.
 */
export const getMemoBody = (memo: string | null): string | null => {
  if (!memo) {
    return null;
  }

  const trimmed = memo.trim();
  const bracket = /^\[([^\]]+)\]/.exec(trimmed);

  if (!bracket) {
    return trimmed || null;
  }

  let residue = bracket[1].replace(DISCOUNT_TOKEN, ' ');
  SOURCE_KEYWORDS.forEach((keyword) => {
    residue = residue.split(keyword).join(' ');
  });
  residue = residue.replace(/[/,·|]/g, ' ').trim();

  const body = residue ? trimmed : trimmed.slice(bracket[0].length).trim();

  return body || null;
};

export const extractScheduleMeta = (memo: string | null): ScheduleMeta => {
  const empty: ScheduleMeta = { couponLabel: null, sourceLabels: [] };
  if (!memo) {
    return empty;
  }

  const bracket = /^\[([^\]]+)\]/.exec(memo.trim());
  if (!bracket) {
    return empty;
  }

  const inner = bracket[1];
  const coupon = /(\d+(?:\.\d+)?)\s*%/.exec(inner);

  return {
    couponLabel: coupon ? `${coupon[1]}%` : null,
    sourceLabels: SOURCE_KEYWORDS.filter((keyword) => inner.includes(keyword)),
  };
};

/** 요일 라벨 — 월요일 시작 (0=일요일인 dayjs().day() 로 인덱싱). */
export const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export const getWeekdayLabel = (date: string): string => WEEKDAY_LABELS[dayjs(date).day()];

/**
 * 날짜의 종류. 기존 구글시트 색상 관례를 그대로 승계한다 —
 * **토요일은 파란색 계열, 일요일과 공휴일은 빨간색 계열.**
 * 공휴일이 토요일과 겹치면 공휴일(빨강)이 우선한다.
 */
export const DAY_TYPE = {
  HOLIDAY: 'holiday',
  SUNDAY: 'sunday',
  SATURDAY: 'saturday',
  WEEKDAY: 'weekday',
} as const;

export type DayType = (typeof DAY_TYPE)[keyof typeof DAY_TYPE];

export const getDayType = (date: string, holidayDates: Set<string>): DayType => {
  if (holidayDates.has(date)) {
    return DAY_TYPE.HOLIDAY;
  }

  const dayOfWeek = dayjs(date).day();

  if (dayOfWeek === 0) {
    return DAY_TYPE.SUNDAY;
  }
  if (dayOfWeek === 6) {
    return DAY_TYPE.SATURDAY;
  }
  return DAY_TYPE.WEEKDAY;
};

/**
 * 요일/공휴일 색상 팔레트. 간트·캘린더가 같은 값을 쓰도록 한 곳에 둔다.
 *
 * - `headerText`: 간트 일자 헤더 (작은 글씨라 평일은 회색)
 * - `dayNumberText`: 캘린더 날짜 숫자 (평일도 본문 톤으로 진하게)
 */
export const DAY_TYPE_COLOR: Record<
  DayType,
  { headerText: string; dayNumberText: string; background: string; stripe: string }
> = {
  [DAY_TYPE.HOLIDAY]: {
    headerText: '#dc2626',
    dayNumberText: '#dc2626',
    background: '#fee2e2',
    stripe: 'rgba(220,38,38,0.07)',
  },
  [DAY_TYPE.SUNDAY]: {
    headerText: '#dc2626',
    dayNumberText: '#dc2626',
    background: '#fef2f2',
    stripe: 'rgba(220,38,38,0.05)',
  },
  [DAY_TYPE.SATURDAY]: {
    headerText: '#2563eb',
    dayNumberText: '#2563eb',
    background: '#eff6ff',
    stripe: 'rgba(37,99,235,0.05)',
  },
  [DAY_TYPE.WEEKDAY]: {
    headerText: '#6b7280',
    dayNumberText: '#374151',
    background: 'transparent',
    stripe: 'transparent',
  },
};
